import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import Card from "@mui/material/Card";
import Chip from "@mui/material/Chip";
import Icon from "@mui/material/Icon";
import Tooltip from "@mui/material/Tooltip";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";
import useApi from "components/Utils/api";
import toast from "react-hot-toast";
import PermissionControl from "components/Utils/PermissionControl";

const statusColor = { ACTIVE: "success", INACTIVE: "default", SUSPENDED: "error" };

// ── Cell renderers ───────────────────────────────────────────────────────────

function StatusCell({ cell }) {
  return <Chip label={cell.value} color={statusColor[cell.value] || "default"} size="small" />;
}
StatusCell.propTypes = { cell: PropTypes.shape({ value: PropTypes.string }) };

function DefaultCell({ cell }) {
  return cell.value ? <Chip label="Default" color="primary" size="small" /> : null;
}
DefaultCell.propTypes = { cell: PropTypes.shape({ value: PropTypes.bool }) };

function SettlementModeCell({ cell }) {
  return (
    <Chip
      label={cell.value || "INTERNAL"}
      color={cell.value === "EXTERNAL" ? "warning" : "default"}
      size="small"
    />
  );
}
SettlementModeCell.propTypes = { cell: PropTypes.shape({ value: PropTypes.string }) };

function ActionsCell({ row, onSetDefault, onToggleStatus, onView }) {
  const { id, isDefault, status } = row.original;
  return (
    <MDBox display="flex" gap={1} alignItems="center">
      <Tooltip title="View Detail" arrow>
        <div>
          <RemoveRedEyeIcon onClick={() => onView(id)} style={{ cursor: "pointer", fontSize: 20 }} />
        </div>
      </Tooltip>
      <PermissionControl permission={{ module: "SettlementRoutes", subModule: "Update" }}>
        <Tooltip title={isDefault ? "Already default" : "Set as Default"} arrow>
          <span>
            <MDButton size="small" variant="outlined" color="warning" iconOnly disabled={isDefault} onClick={() => onSetDefault(id)}>
              <Icon>star</Icon>
            </MDButton>
          </span>
        </Tooltip>
      </PermissionControl>
      <PermissionControl permission={{ module: "SettlementRoutes", subModule: "Update" }}>
        <Tooltip title={status === "ACTIVE" ? "Deactivate" : "Activate"} arrow>
          <MDButton size="small" variant="outlined" color={status === "ACTIVE" ? "error" : "success"} iconOnly onClick={() => onToggleStatus(id, status)}>
            <Icon>{status === "ACTIVE" ? "pause" : "play_arrow"}</Icon>
          </MDButton>
        </Tooltip>
      </PermissionControl>
    </MDBox>
  );
}
ActionsCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      id: PropTypes.string,
      isDefault: PropTypes.bool,
      status: PropTypes.string,
    }),
  }),
  onSetDefault: PropTypes.func.isRequired,
  onToggleStatus: PropTypes.func.isRequired,
  onView: PropTypes.func.isRequired,
};

// ── Page ─────────────────────────────────────────────────────────────────────

export default function SettlementRoutesList() {
  const api = useApi();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);

  const load = () => {
    api.get("settlement-routes")
      .then((res) => setRows(res.data || []))
      .catch(() => toast.error("Failed to load settlement routes"));
  };

  useEffect(() => { load(); }, []);

  const handleSetDefault = (id) => {
    api.put(`settlement-routes/${id}/default`)
      .then(() => { toast.success("Default route updated"); load(); })
      .catch(() => toast.error("Failed to update default route"));
  };

  const handleStatusToggle = (id, currentStatus) => {
    const newStatus = currentStatus === "ACTIVE" ? "INACTIVE" : "ACTIVE";
    api.put(`settlement-routes/${id}/status?status=${newStatus}`)
      .then(() => { toast.success(`Route ${newStatus.toLowerCase()}`); load(); })
      .catch(() => toast.error("Failed to update status"));
  };

  const columns = [
    { Header: "Name",             accessor: "name",           width: "22%", align: "left" },
    { Header: "Bank",             accessor: "bankName",        width: "14%", align: "left" },
    { Header: "Mode",             accessor: "mode",            width: "8%",  align: "left" },
    { Header: "Settlement",       accessor: "settlementMode",  width: "14%", align: "left", Cell: SettlementModeCell },
    { Header: "Status",           accessor: "status",          width: "10%", align: "left", Cell: StatusCell },
    { Header: "Default",          accessor: "isDefault",       width: "8%",  align: "center", Cell: DefaultCell },
    {
      Header: "Actions",
      accessor: "actions",
      width: "24%",
      align: "left",
      // eslint-disable-next-line react/prop-types
      Cell: ({ row }) => (
        <ActionsCell
          row={row}
          onView={(id) => navigate(`/settlement-routes/${id}`)}
          onSetDefault={handleSetDefault}
          onToggleStatus={handleStatusToggle}
        />
      ),
    },
  ];

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Card>
          <MDBox p={3} display="flex" justifyContent="space-between" alignItems="center">
            <MDTypography variant="h5">Settlement Routes</MDTypography>
            <PermissionControl permission={{ module: "SettlementRoutes", subModule: "Create" }}>
              <MDButton variant="gradient" color="info" startIcon={<Icon>add</Icon>} onClick={() => navigate("/settlement-routes/new")}>
                New Route
              </MDButton>
            </PermissionControl>
          </MDBox>
          <DataTable table={{ columns, rows }} showTotalEntries isSorted noEndBorder entriesPerPage canSearch />
        </Card>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}
